import {
  CardStackPlusIcon,
  ChatBubbleIcon,
  ExitIcon,
  GearIcon,
  HamburgerMenuIcon,
  StarIcon,
} from '@radix-ui/react-icons';
import { Badge, Button, DropdownMenu, IconButton } from '@radix-ui/themes';
import { Link, useNavigate } from 'react-router-dom';
import magazineLogo from 'assets/images/tt_magazine.png';
import { getAPIToken, isCurrentUserPremium, isLoggedIn } from 'helpers/authentication';
import { openMagazine } from 'helpers/utils';
import { logout } from 'store/authentication/authenticationSlice';
import { useAppDispatch } from 'store/store';

export const DropDownMenu: React.FC<{ iconClassName: string; message_counter: number; isMobileOnly: boolean }> = ({
  iconClassName,
  message_counter,
  isMobileOnly,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const logoutFunction = () => {
    const token = getAPIToken();
    dispatch(logout(token));
  };

  const hasMessage = message_counter > 0;

  const loggedItemsClassName = 'h-10 py-2 px-3 gap-2 hover:bg-accent-accent-3 cursor-pointer';
  const messageItem = (
    <DropdownMenu.Item className={loggedItemsClassName} onClick={() => navigate('/profilo/messaggi')}>
      <ChatBubbleIcon width='18' height='18' />
      Messaggi
      {hasMessage && <Badge className='bg-semantic-error-9 text-white'>{message_counter}</Badge>}
    </DropdownMenu.Item>
  );

  const gearItem = (
    <DropdownMenu.Item className={loggedItemsClassName} onClick={() => navigate('/profilo/modifica')}>
      <GearIcon width='18' height='18' />
      Impostazioni
    </DropdownMenu.Item>
  );

  const manageAdditionalMatchesItem = (
    <DropdownMenu.Item className={loggedItemsClassName} onClick={() => navigate('/profilo/partite-aggiuntive')}>
      <CardStackPlusIcon width='18' height='18' />
      Gestisci partite e bonus
    </DropdownMenu.Item>
  );

  const manageMembershipItem = (
    <DropdownMenu.Item
      className='h-10 cursor-pointer gap-2 rounded-md bg-accent-accent-3 px-3 py-2'
      onClick={() => navigate('/club-membership')}
    >
      <StarIcon width='18' height='18' />
      Gestisci Membership
    </DropdownMenu.Item>
  );

  const becomeMemberItem = (
    <DropdownMenu.Item className='my-6 cursor-pointer px-0' onClick={() => navigate('/club-membership')}>
      <Button className='flex h-auto w-full cursor-pointer flex-col gap-2 rounded-md bg-accent-accent-6 p-3 hover:bg-accent-accent-3'>
        <div className='flex gap-2'>
          <StarIcon width='18' height='18' />
          Diventa Club Member
        </div>
        <div className='text-xs text-accent-accent-10'>
          Accedi alle classifiche premium, rimuovi le pubblicità e molto altro!
        </div>
      </Button>
    </DropdownMenu.Item>
  );

  const signInItem = (
    <DropdownMenu.Item className='my-6 cursor-pointer px-0' onClick={() => navigate('/accedi')}>
      <Button className='flex h-auto w-full cursor-pointer flex-col gap-2 rounded-md bg-accent-accent-6 p-3 hover:bg-accent-accent-3'>
        <div className='flex gap-2'>
          <StarIcon width='18' height='18' />
          Iscriviti a Tennistalker
        </div>
        <div className='text-xs text-accent-accent-10'>
          Tutte le partite, i tornei e le classifiche del tennis Italiano.
        </div>
      </Button>
    </DropdownMenu.Item>
  );

  const magazineItem = (
    <DropdownMenu.Item className='my-4 cursor-pointer hover:bg-transparent' onClick={openMagazine}>
      <div>
        <Button className='h-10 w-full gap-2 rounded-md bg-semantic-error-alpha-3 px-3 py-2 hover:bg-accent-accent-3'>
          <img src={magazineLogo} alt='tennistalker magazine' height='18' />
        </Button>
      </div>
    </DropdownMenu.Item>
  );

  const logoutItem = (
    <DropdownMenu.Item className='h-10 gap-2 px-3 py-2 hover:bg-neutral-neutral-3'>
      <div
        className='flex cursor-pointer gap-2 rounded-md bg-neutral-neutral-alpha-3 px-3 py-1 hover:bg-neutral-neutral-alpha-8'
        onClick={logoutFunction}
      >
        <ExitIcon width='18' height='18' />
        Logout
      </div>
    </DropdownMenu.Item>
  );

  const lastItemsClassName = 'hover:underline text-neutral-neutral-8 cursor-pointer';
  const lastItems = (
    <>
      <Link to='/chi-siamo' className={lastItemsClassName}>
        Chi siamo
      </Link>
      •
      <Link to='/contattaci' className={lastItemsClassName}>
        Contattaci
      </Link>
      •
      <Link to='/faq' className={lastItemsClassName}>
        FAQ
      </Link>
      •
      <Link to='/privacy-policy' className={lastItemsClassName}>
        Privacy Policy
      </Link>
      •
      <Link to='/condizioni-di-utilizzo' className={lastItemsClassName}>
        Condizioni Generali
      </Link>
    </>
  );

  const commonParts = (
    <>
      {isMobileOnly && magazineItem}

      <DropdownMenu.Separator />
      <DropdownMenu.Item className='cursor-pointer hover:bg-accent-accent-3' onClick={() => navigate('/contattaci')}>
        Contatti e aiuto
      </DropdownMenu.Item>

      {logoutItem}

      <div className='mt-2 flex flex-wrap gap-2 p-2 text-xs text-neutral-neutral-8'>{lastItems}</div>
    </>
  );

  const membershipItem = isCurrentUserPremium() ? manageMembershipItem : becomeMemberItem;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton variant='ghost' className={iconClassName} aria-label='Menu'>
          <HamburgerMenuIcon width='18' height='18' />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className='w-64 bg-neutral-neutral-3'>
        {isLoggedIn() ? (
          <>
            {messageItem}
            {gearItem}
            {manageAdditionalMatchesItem}
            {membershipItem}
          </>
        ) : (
          signInItem
        )}

        {commonParts}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default DropDownMenu;
